import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';
import { Router } from '@angular/router';

import { AlertController, AlertOptions } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';

import {
  cloneDeep,
  compact,
  deburr,
  each,
  filter as lodashFilter,
  first,
  has,
  isEmpty,
  isEqual,
  isNil,
  isNull,
  isNumber,
  isUndefined,
  map as lodashMap,
  orderBy,
  replace
} from 'lodash-es';
import { DateTime } from 'luxon';
import { forkJoin, map, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, finalize } from 'rxjs/operators';
import { v4 } from 'uuid';

import { MiddlewareError } from '../../classes/middleware-error';
import { defaultPropertyProfileExportOptions } from '../../constants/profile.constant';
import { AddressEnum } from '../../enums/address';
import { DataProviderIdEnum } from '../../enums/data-provider';
import { KeyRefreshEnum } from '../../enums/key-refresh';
import { ProfileSearchEnum } from '../../enums/profile-search';
import { PropertyProfileTemplateEnum } from '../../enums/property-report';
import { getMainRouteByPropertySearchType } from '../../helpers/property-profile';
import { AddressPipe } from '../../misc/pipes/address.pipe';
import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { FileService } from '../../misc/services/file.service';
import { LoadingService } from '../../misc/services/loading.service';
import { PropertyForceApiService } from '../../misc/services/propertyForceApi.service';
import { ReportApiService } from '../../misc/services/reportApi.service';
import { FindByAddressResponse, RefreshKeyResponse } from '../../typings/property-force-api';
import * as fromMapActions from '../actions/map.actions';
import * as fromPropertyProfileActions from '../actions/property-profile.actions';
import {
  ParsedSchool,
  ParsedSchoolData,
  ParsedSchoolMarker,
  ProfileComparableSale,
  ProfileSchools,
  PropertyProfile,
  PropertyProfileHistoryItem
} from '../models/property-profile.model';
import * as fromDataProviderSelectors from '../selectors/data-provider.selectors';
import * as fromDeviceSelectors from '../selectors/device.selectors';
import * as fromPropertyProfileSelectors from '../selectors/property-profile.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';
import * as fromUserSelectors from '../selectors/user.selectors';

@Injectable()
export class PropertyProfileEffects {
  private actions$ = inject(Actions);
  private addressPipe = inject(AddressPipe);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private fileService = inject(FileService);
  private loadingService = inject(LoadingService);
  private mapGeocoder = inject(MapGeocoder);
  private propertyForceApiService = inject(PropertyForceApiService);
  private reportApiService = inject(ReportApiService);
  private router = inject(Router);
  private store = inject(Store);

  geocodeCoordinatesLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.geocodeCoordinatesLocation),
      filter(({ payload: { latLng } }) => !!latLng.lat && !!latLng.lng),
      concatMap(({ payload: { focus, latLng } }) =>
        this.mapGeocoder
          .geocode({
            location: latLng
          })
          .pipe(
            switchMap(({ results, status }) => {
              let actions: Action[];
              if (status === google.maps.GeocoderStatus.OK && !isEmpty(results)) {
                const result = results[0];
                actions = [
                  fromPropertyProfileActions.geocodeLocationSuccess({
                    payload: { result }
                  }),
                  fromPropertyProfileActions.updatePropertySearchLocation({
                    payload: {
                      address: result.formatted_address,
                      location: result.geometry.location.toJSON()
                    }
                  })
                ];
                if (focus) {
                  actions.push(
                    fromMapActions.updateCenter({
                      payload: { location: result.geometry.location.toJSON() }
                    })
                  );
                }
              } else {
                actions = [
                  fromPropertyProfileActions.geocodeLocationFailure({
                    payload: {
                      error: new Error(
                        `No location data could be found for the provided coordinates. Please make sure you selected a valid address.`
                      )
                    }
                  })
                ];
              }
              return actions;
            }),
            catchError((error: Error) => of(fromPropertyProfileActions.geocodeLocationFailure({ payload: { error } })))
          )
      )
    )
  );

  geocodeAddressLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.geocodeAddressLocation),
      filter(({ payload: { address } }) => !!address),
      concatMap(({ payload: { address } }) =>
        this.mapGeocoder
          .geocode({
            address,
            componentRestrictions: {
              country: 'USA'
            }
          })
          .pipe(
            switchMap(({ results, status }) => {
              let actions: Action[];
              if (status === google.maps.GeocoderStatus.OK && !isEmpty(results)) {
                let result = results[0];
                if (results.length > 1) {
                  const streetResults = lodashFilter(results, (result) => result.types.includes('street_address'));
                  if (streetResults.length > 0) {
                    result = streetResults[0];
                    const addressResults = lodashFilter(results, (result) => isEqual(result.types, ['street_address']));
                    if (addressResults.length > 0) {
                      result = addressResults[0];
                    }
                  }
                }
                actions = [
                  fromPropertyProfileActions.geocodeLocationSuccess({
                    payload: { result }
                  }),
                  fromPropertyProfileActions.updatePropertySearchLocation({
                    payload: { address: result.formatted_address, location: result.geometry.location.toJSON() }
                  }),
                  fromMapActions.updateCenter({
                    payload: {
                      location: result.geometry.location.toJSON()
                    }
                  })
                ];
              } else {
                actions = [
                  fromPropertyProfileActions.geocodeLocationFailure({
                    payload: {
                      error: new Error(
                        `No location data could be found for "${address}". Please make sure you typed a valid address.`
                      )
                    }
                  })
                ];
              }
              return actions;
            }),
            catchError((error: Error) => of(fromPropertyProfileActions.geocodeLocationFailure({ payload: { error } })))
          )
      )
    )
  );

  geocodeLocationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.geocodeLocationFailure),
        concatMap(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Search Error',
            message: error.message
          });
          await alert.present();
        })
      ),
    { dispatch: false }
  );

  refreshKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.refreshKey),
      withLatestFrom(
        this.store.pipe(select(fromDataProviderSelectors.selectDataProviderCredentials)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode))
      ),
      map((result) => ({
        deviceData: result[2],
        office: result[3],
        password: result[1].password ?? '',
        username: result[1].username ?? ''
      })),
      switchMap((query) =>
        this.propertyForceApiService.getData<RefreshKeyResponse>('/GetKeyWithTACCheck', query).pipe(
          switchMap((response) =>
            of(fromPropertyProfileActions.refreshKeySuccess({ payload: { key: response.d.Key } }))
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              fromPropertyProfileActions.refreshKeyFailure({
                payload: {
                  error
                }
              })
            )
          )
        )
      )
    )
  );

  loadProfileByAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.loadProfileByAddress),
      withLatestFrom(
        this.store.pipe(select(fromPropertyProfileSelectors.selectKey)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
      ),
      switchMap(
        ([
          {
            payload: { address, searchType, zip }
          },
          key,
          deviceData
        ]) =>
          this.propertyForceApiService
            .getData<FindByAddressResponse>(
              '/FindByAddress',
              {
                address: deburr(address),
                deviceData,
                key,
                zip
              },
              {
                keyRefreshType: KeyRefreshEnum.PropertyProfile,
                shouldConvertStatusCodeErrors: true
              }
            )
            .pipe(
              map((response) =>
                isEmpty(response.d.Locations)
                  ? fromPropertyProfileActions.savePropertyProfileHistory({
                      payload: {
                        propertyProfile: response.d,
                        searchType
                      }
                    })
                  : fromPropertyProfileActions.goToMultipleProfilesSelection({
                      payload: {
                        locations: response.d.Locations,
                        searchType
                      }
                    })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromPropertyProfileActions.loadProfileFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  loadProfileByAPN$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.loadProfileByAPN),
      withLatestFrom(
        this.store.pipe(select(fromPropertyProfileSelectors.selectKey)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
      ),
      switchMap(
        ([
          {
            payload: { apn, county, searchType, state }
          },
          key,
          deviceData
        ]) =>
          this.propertyForceApiService
            .getData<FindByAddressResponse>(
              '/FindByAPN',
              {
                apn,
                county,
                deviceData,
                key,
                state
              },
              {
                keyRefreshType: KeyRefreshEnum.PropertyProfile,
                shouldConvertStatusCodeErrors: true
              }
            )
            .pipe(
              map((response) =>
                fromPropertyProfileActions.savePropertyProfileHistory({
                  payload: {
                    propertyProfile: response.d,
                    searchType
                  }
                })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromPropertyProfileActions.loadProfileFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  loadProfileByOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.searchByOwner),
      withLatestFrom(
        this.store.pipe(select(fromPropertyProfileSelectors.selectKey)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
      ),
      map(
        ([
          {
            payload: { county, firstName, lastName, state, zip }
          },
          key,
          deviceData
        ]) => {
          const query: {
            county: string;
            deviceData: string;
            firstName?: string;
            key: string;
            lastName: string;
            state: string;
            zip?: string;
          } = {
            county,
            deviceData,
            key,
            lastName,
            state
          };
          if (!isEmpty(firstName)) {
            query.firstName = firstName;
          }
          if (!isEmpty(zip)) {
            query.zip = zip;
          }
          return query;
        }
      ),
      switchMap((query) =>
        this.propertyForceApiService
          .getData<FindByAddressResponse>('/FindByOwner', query, {
            keyRefreshType: KeyRefreshEnum.PropertyProfile,
            shouldConvertStatusCodeErrors: true
          })
          .pipe(
            map((response) =>
              isEmpty(response.d.Locations)
                ? fromPropertyProfileActions.savePropertyProfileHistory({
                    payload: {
                      propertyProfile: response.d,
                      searchType: ProfileSearchEnum.PropertySearch
                    }
                  })
                : fromPropertyProfileActions.goToMultipleProfilesSelection({
                    payload: {
                      locations: response.d.Locations,
                      searchType: ProfileSearchEnum.PropertySearch
                    }
                  })
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromPropertyProfileActions.loadProfileFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  parseSavedPropertyProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.parseSavedPropertyProfile),
      map(({ payload }) => ({
        ...payload,
        loadingId: v4()
      })),
      tap(({ loadingId }) => {
        this.loadingService.setLoading(loadingId, true);
      }),
      switchMap(({ id, loadingId, propertyProfile, searchType }) => {
        const schoolData = this.getSchoolsData(propertyProfile.schools);

        return forkJoin({
          comparableSales:
            (propertyProfile.ComparableSales?.length
              ? forkJoin(
                  this.getEntityGeocodeData<ProfileComparableSale>(propertyProfile.ComparableSales, [
                    'SiteUnit',
                    'SiteUnitType',
                    'SiteAddress',
                    'SiteCity',
                    'siteState',
                    'siteZip'
                  ])
                )
              : of([])) ?? of([]),
          data: of({ id, propertyProfile }),
          isPlatMapAvailable: of(
            !isNil(propertyProfile?.PlatMapType) &&
              ['NONE', 'DISCARDED', ''].indexOf(propertyProfile.PlatMapType) === -1
          ),
          loadingId: of(loadingId),
          schoolMarkers: schoolData.pipe(map((data) => data.markers)),
          schools: schoolData.pipe(map((data) => data.schools)),
          searchType: of(searchType)
        });
      }),
      concatMap(({ comparableSales, data, isPlatMapAvailable, loadingId, schoolMarkers, schools, searchType }) =>
        isNumber(data.propertyProfile.latitude) && isNumber(data.propertyProfile.longitude)
          ? of({
              comparableSales,
              data,
              isPlatMapAvailable,
              loadingId,
              schoolMarkers,
              schools,
              searchType
            })
          : forkJoin({
              comparableSales: of(comparableSales),
              data: this.mapGeocoder
                .geocode({
                  address: this.addressPipe.transform(data.propertyProfile, AddressEnum.Geocode),
                  componentRestrictions: {
                    country: 'USA'
                  }
                })
                .pipe(
                  map(({ results, status }) => {
                    const geocoderResult = first(results);
                    return status === google.maps.GeocoderStatus.OK && geocoderResult
                      ? {
                          id: data.id,
                          propertyProfile: {
                            ...data.propertyProfile,
                            latitude: `${geocoderResult.geometry.location.lat()}`,
                            longitude: `${geocoderResult.geometry.location.lng()}`
                          }
                        }
                      : {
                          id: data.id,
                          propertyProfile: data.propertyProfile
                        };
                  })
                ),
              isPlatMapAvailable: of(isPlatMapAvailable),
              loadingId: of(loadingId),
              schoolMarkers: of(schoolMarkers),
              schools: of(schools),
              searchType: of(searchType)
            })
      ),
      map(({ comparableSales, data, isPlatMapAvailable, loadingId, schoolMarkers, schools, searchType }) => ({
        loadingId,
        searchType,
        storedPropertyProfile: {
          comparableSales,
          hasSchoolsData: !!schools.length,
          isPlatMapAvailable,
          ...defaultPropertyProfileExportOptions,
          propertyProfile: data.propertyProfile,
          reportId: data.id,
          schoolMarkers,
          schools
        }
      })),
      tap(({ loadingId }) => {
        this.loadingService.setLoading(loadingId, false);
      }),
      concatMap(({ searchType, storedPropertyProfile }) => [
        fromPropertyProfileActions.setPropertyProfile({
          payload: { storedPropertyProfile }
        }),
        fromPropertyProfileActions.setSelectPropertyProfile({
          payload: { storedPropertyProfile }
        }),
        fromPropertyProfileActions.goToSelectedPropertyProfile({ payload: { searchType } })
      ])
    )
  );

  savePropertyProfileHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.savePropertyProfileHistory),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectParseType)),
        this.store.pipe(select(fromSettingSelectors.selectApplicationKey)),
        this.store.pipe(select(fromUserSelectors.selectEmail))
      ),
      switchMap(
        ([
          {
            payload: { propertyProfile, searchType }
          },
          parseType,
          appName,
          email
        ]) => {
          const body: {
            address: string;
            appName: string;
            displayCity: string;
            displayOwnerNames: string;
            displayStateCode: string;
            displayStreetAddress: string;
            email: string;
            reportContent: {
              d: PropertyProfile;
            };
            reportVersion: number;
          } = {
            address: this.addressPipe.transform(propertyProfile, AddressEnum.Full),
            appName: appName ?? '',
            displayCity: propertyProfile.SiteCity ?? '',
            displayOwnerNames: propertyProfile.PrimaryOwnerName ?? '',
            displayStateCode: propertyProfile.SiteState ?? '',
            displayStreetAddress: this.addressPipe.transform(propertyProfile, AddressEnum.LineOne),
            email: email || '',
            reportContent: {
              d: propertyProfile
            },
            reportVersion: 1
          };

          return this.adminPortalApiService
            .sendRequest('POST', `/v2/property-profiles`, {
              body,
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map((data: { id: string }) =>
                fromPropertyProfileActions.parseSavedPropertyProfile({
                  payload: { id: data.id, propertyProfile, searchType }
                })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromPropertyProfileActions.savePropertyProfileHistoryFailure({
                    payload: { error }
                  })
                )
              )
            );
        }
      )
    )
  );

  goToSelectedPropertyProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.goToSelectedPropertyProfile),
        withLatestFrom(this.store.pipe(select(fromPropertyProfileSelectors.selectCurrentPropertyProfileId))),
        filter((results) => !isNull(results[1])),
        concatMap(
          ([
            {
              payload: { searchType }
            },
            reportId
          ]) => this.router.navigate([getMainRouteByPropertySearchType(searchType), 'profile', reportId])
        )
      ),
    {
      dispatch: false
    }
  );

  loadProfileFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.loadProfileFailure),
        concatMap(async ({ payload: { error } }) => {
          let options: AlertOptions;

          if (error instanceof MiddlewareError) {
            options = error.alertOptions;
          } else {
            options = cloneDeep(MiddlewareError.defaultAlertOptions);
            options.message = error.message;
          }

          const alert = await this.alertController.create(options);
          await alert.present();
          await alert.onDidDismiss();
        })
      ),
    {
      dispatch: false
    }
  );

  loadByAPNFromMultipleProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.goToMultipleProfilesSelection),
      filter(({ payload: { locations } }) => locations.length === 1),
      map(({ payload: { locations, searchType } }) => {
        const location = first(locations);
        return fromPropertyProfileActions.loadProfileByAPN({
          payload: {
            apn: location?.APN ?? '',
            county: location?.county ?? '',
            searchType,
            state: location?.State ?? ''
          }
        });
      })
    )
  );

  goToMultipleProfilesSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.goToMultipleProfilesSelection),
        filter(({ payload: { locations } }) => locations.length > 1),
        concatMap(async ({ payload: { locations, searchType } }) =>
          this.router.navigate([getMainRouteByPropertySearchType(searchType), 'multiple-results'], {
            state: {
              locations
            }
          })
        )
      ),
    {
      dispatch: false
    }
  );

  emailPlatMap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.emailPlatMap),
        withLatestFrom(
          this.store.pipe(select(fromPropertyProfileSelectors.selectKey)),
          this.store.pipe(
            select(fromDataProviderSelectors.selectId),
            map((providerId) => providerId ?? 'UNKNOWN')
          ),
          this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
        ),
        map(
          ([
            {
              payload: { address, deliveryEmail, zip }
            },
            key,
            providerId,
            deviceData
          ]) => ({
            address,
            deliveryEmail,
            deviceData,
            key,
            providerId,
            zip
          })
        ),
        switchMap((query) =>
          this.propertyForceApiService.getData<void>('/GetMapDT', query, {
            keyRefreshType: KeyRefreshEnum.PropertyProfile,
            shouldConvertStatusCodeErrors: true
          })
        )
      ),
    {
      dispatch: false
    }
  );

  emailDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.emailDocument),
        withLatestFrom(
          this.store.pipe(select(fromPropertyProfileSelectors.selectKey)),
          this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData)),
          this.store.pipe(select(fromDataProviderSelectors.selectId))
        ),
        map(
          ([
            {
              payload: {
                apn,
                city,
                cmtIdentifier,
                documentNumber,
                documentType,
                email,
                fips,
                message,
                recodingDate,
                stateCode,
                streetAddress,
                subject
              }
            },
            key,
            deviceData,
            dataProviderId
          ]) => {
            const query: {
              body: string;
              deviceData: string;
              docNum: string;
              email: string;
              key: string;
              parameters: string;
              subject: string;
              type: string;
            } = {
              body: message,
              deviceData,
              docNum: documentNumber,
              email,
              key,
              parameters: '',
              subject,
              type: documentType
            };

            switch (dataProviderId) {
              case DataProviderIdEnum.DataTrace:
                query.parameters = JSON.stringify({
                  city,
                  cmtIdentifier,
                  stateCode,
                  streetAddress
                });
                break;
              case DataProviderIdEnum.DataTraceDTAPI:
                query.parameters = JSON.stringify({
                  city,
                  cmtIdentifier,
                  fips,
                  stateCode,
                  streetAddress
                });
                break;
              default:
                query.parameters = JSON.stringify({
                  apn,
                  fips,
                  recDate: DateTime.fromJSDate(new Date(recodingDate)).toFormat('yyyy/MM/dd')
                });
                break;
            }
            return query;
          }
        ),
        switchMap((query) =>
          this.propertyForceApiService
            .getData('/SendDocumentImage', query, {
              keyRefreshType: KeyRefreshEnum.PropertyProfile,
              shouldConvertStatusCodeErrors: true
            })
            .pipe(
              map(async (response) => {
                const alert = await this.alertController.create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Success',
                  message: `Your request was successfully received and we’ve begun generating your PDF document. When it is complete, we’ll send it to ${query.email}`
                });

                await alert.present();
                return response;
              }),
              catchError(async (error) => {
                const alert = await this.alertController.create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Oops',
                  message: `We are sorry, but there was a issue processing your request (${error.message}).`
                });

                await alert.present();
                return error;
              })
            )
        )
      ),
    {
      dispatch: false
    }
  );

  loadReportHtml$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.loadReportHtml),
      concatLatestFrom((action) => [
        this.store.select(fromSettingSelectors.selectReferralCode),
        this.store.select(fromPropertyProfileSelectors.selectPropertyProfileExportOptions(action.payload.reportId))
      ]),
      filter((data) => !isNull(data[1])),
      switchMap(
        ([
          {
            payload: { reportId }
          },
          referralCode,
          exportOptions
        ]) =>
          this.reportApiService
            .sendRequest('POST', '/v4/property-profiles/export/html', {
              body: {
                options: {
                  content: {
                    detailsAndCharacteristics: {
                      visible: exportOptions.isDetailsAndCharacteristicsVisible
                    },
                    platMap: {
                      visible: exportOptions.isPlatMapVisible
                    },
                    saleInformation: {
                      visible: exportOptions.isSaleInformationVisible
                    },
                    schools: {
                      visible: exportOptions.isSchoolsVisible
                    },
                    transactionHistory: {
                      visible: exportOptions.isTransactionHistoryVisible
                    }
                  },
                  template: PropertyProfileTemplateEnum.Modern
                },
                referralCode: referralCode ?? '',
                reportId
              }
            })
            .pipe(
              map((reportHtml: string) =>
                fromPropertyProfileActions.loadReportHtmlSuccess({
                  payload: {
                    reportHtml,
                    reportId
                  }
                })
              ),
              catchError(() => of(fromPropertyProfileActions.loadReportHtmlFailure()))
            )
      )
    )
  );

  exportReportPdf$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPropertyProfileActions.exportReportPdf),
        concatLatestFrom((action) => [
          this.store.select(fromSettingSelectors.selectReferralCode),
          this.store.select(fromPropertyProfileSelectors.selectPropertyProfileExportOptions(action.payload.reportId))
        ]),
        filter((data) => !isNull(data[1])),
        switchMap(
          ([
            {
              payload: { address, reportId }
            },
            referralCode,
            exportOptions
          ]) =>
            this.reportApiService
              .sendRequest('POST', '/v4/property-profiles/export/pdf', {
                body: {
                  options: {
                    content: {
                      detailsAndCharacteristics: {
                        visible: exportOptions.isDetailsAndCharacteristicsVisible
                      },
                      platMap: {
                        visible: exportOptions.isPlatMapVisible
                      },
                      saleInformation: {
                        visible: exportOptions.isSaleInformationVisible
                      },
                      schools: {
                        visible: exportOptions.isSchoolsVisible
                      },
                      transactionHistory: {
                        visible: exportOptions.isTransactionHistoryVisible
                      }
                    },
                    template: PropertyProfileTemplateEnum.Modern
                  },
                  referralCode: referralCode ?? '',
                  reportId
                },
                responseType: 'blob'
              })
              .pipe(
                map(async (blob: Blob) => {
                  await this.fileService.exportDocument(
                    blob,
                    `${replace(address, /[^a-zA-Z0-9]/gm, '-').toLowerCase()}-${DateTime.now().toFormat('X')}.pdf`,
                    address
                  );
                })
              )
        )
      ),
    {
      dispatch: false
    }
  );

  loadProfileHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.loadProfileHistory),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectParseType)),
        this.store.pipe(select(fromSettingSelectors.selectApplicationKey)),
        this.store.pipe(select(fromUserSelectors.selectEmail))
      ),
      switchMap(
        ([
          {
            payload: { ignoreLoader, refreshedCustomEvent }
          },
          parseType,
          appName,
          email
        ]) =>
          this.adminPortalApiService
            .sendRequest(
              'GET',
              `/v2/property-profiles/history`,
              {
                params: this.adminPortalApiService.getQueryParams({
                  appName: appName || '',
                  email: email || '',
                  parseType: parseType
                })
              },
              {
                ignoreLoader
              }
            )
            .pipe(
              map((payload: PropertyProfileHistoryItem[]) =>
                fromPropertyProfileActions.loadProfileHistorySuccess({ payload })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromPropertyProfileActions.loadProfileHistoryFailure({
                    payload: { error }
                  })
                )
              ),
              finalize(async () => {
                await refreshedCustomEvent?.target.complete();
              })
            )
      )
    )
  );

  deleteProfileHistoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.deleteProfileHistoryItem),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(([{ id }, parseType]) =>
        this.adminPortalApiService
          .sendRequest('DELETE', `/v2/property-profiles/${id}`, {
            params: this.adminPortalApiService.getQueryParams({
              parseType: parseType
            })
          })
          .pipe(
            concatMap(() => [
              fromPropertyProfileActions.deleteProfileHistoryItemSuccess({ payload: { id } }),
              fromPropertyProfileActions.loadProfileHistory({ payload: {} })
            ]),
            catchError((error: HttpErrorResponse) =>
              of(
                fromPropertyProfileActions.deleteProfileHistoryItemFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  loadProfileHistoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPropertyProfileActions.loadProfileHistoryItem),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { id }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/property-profiles/${id}`, {
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map(
                (data: {
                  _id: string;
                  address: string;
                  appName: string;
                  displayCity: string;
                  displayOwnerNames: string;
                  displayStateCode: string;
                  displayStreetAddress: string;
                  id: string;
                  reportContent: {
                    d: PropertyProfile;
                  };
                  reportDate: string;
                  reportVersion: number;
                }) =>
                  fromPropertyProfileActions.parseSavedPropertyProfile({
                    payload: {
                      id,
                      propertyProfile: data.reportContent.d,
                      searchType: ProfileSearchEnum.PropertySearch
                    }
                  })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromPropertyProfileActions.loadProfileHistoryItemFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  private getSchoolsData(schools?: ProfileSchools | null) {
    let response: Observable<{
      markers: ParsedSchoolMarker[];
      schools: ParsedSchool[];
    }> = of({
      markers: [],
      schools: []
    });

    if (!isNil(schools)) {
      const orderedSchools = orderBy([...schools.PrivateSchools, ...schools.PublicSchools] as ParsedSchoolData[], [
        (item) => parseFloat(item.Distance ?? '0')
      ]);
      if (orderedSchools.length) {
        response = forkJoin(
          this.getEntityGeocodeData<ParsedSchoolData>(
            orderBy([...schools.PrivateSchools, ...schools.PublicSchools] as ParsedSchoolData[], [
              (item) => parseFloat(item.Distance ?? '0')
            ]),
            ['SchoolAddress', 'SchoolCity', 'SchoolState', 'SchoolZip']
          )
        ).pipe(
          map((geocodedData) => {
            const locationMap = new Map<string, number>();
            const schools: ParsedSchool[] = [];
            const markers: ParsedSchoolMarker[] = [];

            let idCounter = 1;

            each(geocodedData, (item) => {
              const key = `${item.location.lat},${item.location.lng}`;

              let markerId = locationMap.get(key);

              if (isUndefined(markerId)) {
                markerId = idCounter++;
                locationMap.set(key, markerId);

                markers.push({
                  id: markerId,
                  location: item.location
                });
              }

              schools.push({
                addressComponents: item.addressComponents,
                data: item.data,
                markerId
              });
            });

            return { markers, schools };
          })
        );
      }
    }

    return response;
  }

  private getEntityGeocodeData<T>(data: T[], addressSegments: string[]) {
    return lodashMap(
      data,
      (dataEntity) =>
        this.mapGeocoder
          .geocode({
            address: compact([
              ...lodashMap(addressSegments, (key) => (has(dataEntity, key) ? dataEntity[key] : null)),
              'USA'
            ]).join(', '),
            componentRestrictions: {
              country: 'USA'
            }
          })
          .pipe(
            filter(({ status }) => status === google.maps.GeocoderStatus.OK),
            map(({ results }) => {
              const geocoderResult = first(results);
              return !isNil(geocoderResult)
                ? {
                    addressComponents: geocoderResult.address_components,
                    data: dataEntity,
                    location: geocoderResult.geometry.location.toJSON()
                  }
                : null;
            }),
            filter((item) => !isNull(item)),
            catchError((error) => {
              //@TODO: We need to handle the errors here.
              return of(error);
            })
          ) as Observable<{
          addressComponents: google.maps.GeocoderAddressComponent[];
          data: T;
          location: google.maps.LatLngLiteral;
        }>
    );
  }
}
